import { useEffect, useState } from 'react';
import axios from 'axios';
import { Outlet, useLocation } from 'react-router-dom';

import useMediaQuery from '@mui/material/useMediaQuery';
import Container from '@mui/material/Container';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';

import Drawer from 'layout/Dashboard/Drawer';
import Header from 'layout/Dashboard/Header';
import Footer from 'layout/Dashboard/Footer';
import HorizontalBar from 'layout/Dashboard/Drawer/HorizontalBar';
import Loader from 'components/Loader';

import AuthGuard from 'utils/route-guard/AuthGuard';

import { MenuOrientation } from 'config';
import useConfig from 'hooks/useConfig';
import { handlerDrawerOpen } from 'api/menu';

import { apiUrl } from 'api/http';
import { getToken as getAuthToken } from 'api/auth';

// ---- helpers -------------------------------------------------------
function getUserToken() {
  // user area, not admin
  const t = getAuthToken({ admin: false });
  // eslint-disable-next-line no-console
  console.log('[UserDashboard] user token present?', !!t);
  return t || '';
}

// renders a round image as the "icon"
const makeImgIcon = (src, alt = '') =>
  function ImgIcon() {
    const url = src || 'https://via.placeholder.com/48x48.png?text=%20';
    return (
      <Tooltip title={alt} placement="right" arrow>
        <img
          src={url}
          alt={alt}
          style={{ width: 22, height: 22, borderRadius: '50%', objectFit: 'cover', display: 'block' }}
        />
      </Tooltip>
    );
  };

// ONLY pages, no “Dashboard/Pages” item
function buildMenuFromPages(pages = [], base = '/') {
  const join = (b, p) => `${String(b).replace(/\/$/, '')}/${String(p).replace(/^\//, '')}`;

  const children = (pages || []).map((p) => {
    const id = String(p.page_id || p.id);
    const name = String(p.name || 'Untitled');
    const pic = p.picture_url || p.cover?.source || p.picture?.data?.url || '';
    const Icon = makeImgIcon(pic, name);
    return {
      id: `page-${id}`,
      title: name,
      type: 'item',
      url: join(base, `pages/${id}`),
      icon: Icon,
      breadcrumbs: false,
    };
  });

  return {
    items: [
      {
        id: 'group-pages',
        type: 'group',
        children // only page links with photos
      }
    ]
  };
}
// -------------------------------------------------------------------

export default function UserDashboardLayout() {
  const { pathname } = useLocation();

  // Hide all scrollbars globally in this layout while keeping scroll functional
  useEffect(() => {
    const style = document.createElement('style');
    style.id = 'hide-scrollbars';
    style.textContent = `
      *::-webkit-scrollbar { width: 0 !important; height: 0 !important; display: none !important; }
      * { scrollbar-width: none !important; -ms-overflow-style: none !important; }
    `;
    document.head.appendChild(style);
    return () => { document.getElementById('hide-scrollbars')?.remove(); };
  }, []);

  const downXL = useMediaQuery((theme) => theme.breakpoints.down('xl'));
  const downLG = useMediaQuery((theme) => theme.breakpoints.down('lg'));
  const { container, miniDrawer, menuOrientation } = useConfig();
  const isHorizontal = menuOrientation === MenuOrientation.HORIZONTAL && !downLG;

  const [menuItems, setMenuItems] = useState(buildMenuFromPages([], import.meta.env.VITE_APP_BASE_NAME || '/'));
  const [menuLoading, setMenuLoading] = useState(true);
  

  // responsive drawer like before
  useEffect(() => {
    if (!miniDrawer) handlerDrawerOpen(!downXL);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downXL]);

  // load pages and build the sidebar menu with images
  useEffect(() => {
    let alive = true;
    const token = getUserToken();
    if (!token) {
      setMenuItems(buildMenuFromPages([], import.meta.env.VITE_APP_BASE_NAME || '/'));
      setMenuLoading(false);
      return;
    }

    (async () => {
      try {
        const { data } = await axios.get(apiUrl('facebook/pages/'), {
          headers: { Authorization: `Bearer ${token}` }
        });
        const pages = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : data?.results || [];
        if (!alive) return;
        setMenuItems(buildMenuFromPages(pages, import.meta.env.VITE_APP_BASE_NAME || '/'));
      } catch {
        if (!alive) return;
        setMenuItems(buildMenuFromPages([], import.meta.env.VITE_APP_BASE_NAME || '/'));
      } finally {
        if (alive) setMenuLoading(false);
      }
    })();

    return () => { alive = false; };
  }, []);

  if (menuLoading) return <Loader />;

  return (
    <AuthGuard>
      <Box sx={{ display: 'flex', width: '100%' }}>
        <Header />
        {/*  pass our menu override so Drawer shows only page photos + names */}
        {!isHorizontal ? <Drawer menuItems={menuItems} /> : <HorizontalBar menuItems={menuItems} />}

        <Box component="main" sx={{ width: 'calc(100% - 260px)', flexGrow: 1, p: { xs: 2, sm: 3 }, '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none' }}>
          <Toolbar sx={{ mt: isHorizontal ? 8 : 'inherit' }} />
          <Container
            maxWidth={container ? 'lg' : false}
            disableGutters={!container}
            sx={{
              ...(container && { px: { xs: 0, sm: 2 } }),
              position: 'relative',
              minHeight: 'calc(100vh - 110px)',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {pathname !== '/apps/profiles/account/my-account' }
            <Outlet />
            <Footer />
          </Container>
        </Box>
      </Box>
    </AuthGuard>
  );
}
